import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Button,
  useTheme,
  Stack,
} from "@mui/material";
import VerseDrawer from "./VerseDrawer";
import {
  HISTORICAL_DOCUMENTS,
  HIGHLIGHT_COLORS,
  DARK_HIGHLIGHT_COLORS,
} from "./historicalDocs";
import { getHighlight, setHighlight } from "./highlightUtils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

function parseVerseRef(ref) {
  // e.g. "Exodus 20.1" or "Exodus 20.1-Exodus 20.26"
  const match = ref.match(
    /([A-Za-z0-9 ]+) (\d+)\.(\d+)(?:-([A-Za-z0-9 ]+) (\d+)\.(\d+))?/
  );
  if (!match) return null;
  return {
    bookName: match[1],
    chapter: parseInt(match[2]),
    verse: parseInt(match[3]),
  };
}

const TopicalResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [highlightColor, setHighlightColor] = useState(null);
  const [highlightRefresh, setHighlightRefresh] = useState(0);
  const theme = useTheme();

  const highlightColors =
    theme.palette.mode === "dark" ? DARK_HIGHLIGHT_COLORS : HIGHLIGHT_COLORS;

  // Get search param
  const params = new URLSearchParams(location.search);
  const search = params.get("search") || "";

  useEffect(() => {
    if (!search) return;
    setLoading(true);
    setError(null);
    setVerses([]);
    axios
      .get(`${API_URL}/api/topics?search=${encodeURIComponent(search)}`)
      .then((res) => {
        if (!res.data.length) {
          setError("No topic found.");
          setLoading(false);
          return;
        }
        setTopic(res.data[0]);
        // For each verseRef, fetch the verse text
        const fetches = res.data[0].verseRefs.map((ref) => {
          const parsed = parseVerseRef(ref);
          if (!parsed) return Promise.resolve(null);
          return axios.get(`${API_URL}/api/books`).then((booksRes) => {
            const book = booksRes.data.find(
              (b) => b.name.toLowerCase() === parsed.bookName.toLowerCase()
            );
            if (!book) return null;
            return axios
              .get(
                `${API_URL}/api/books/${book.number}/chapters/${parsed.chapter}`
              )
              .then((chapRes) => {
                const verseObj = chapRes.data.verses.find(
                  (v) => v.number === parsed.verse
                );
                return verseObj
                  ? {
                      ...verseObj,
                      bookName: book.name,
                      chapter: parsed.chapter,
                    }
                  : null;
              });
          });
        });
        Promise.all(fetches).then((versesArr) => {
          setVerses(versesArr.filter(Boolean));
          setLoading(false);
        });
      })
      .catch(() => {
        setError("Failed to load topic.");
        setLoading(false);
      });
  }, [search]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key && e.key.startsWith("highlight-")) {
        setHighlightRefresh((r) => r + 1);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleVerseClick = (verse) => {
    setSelectedVerse(verse);
    setDrawerOpen(true);
  };

  const handleNavigateToVerse = (verse) => {
    axios.get(`${API_URL}/api/books`).then((booksRes) => {
      const book = booksRes.data.find((b) => b.name === verse.bookName);
      if (!book) return;
      const bookId = book.name.replace(/ /g, "-");
      navigate(
        `/read/book/${bookId}/chapter/${verse.chapter}/verse/${verse.number}`
      );
    });
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          Topical Search: {search}
        </Typography>
      </Box>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {topic && !loading && !error && (
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {topic.topic}
          </Typography>
          <Box>
            {verses.map((verse, idx) => {
              const colorIndex = getHighlight(
                verse.bookName,
                verse.chapter,
                verse.number
              );
              const bgColor =
                typeof colorIndex === "number" &&
                colorIndex >= 0 &&
                colorIndex < highlightColors.length
                  ? highlightColors[colorIndex]
                  : "inherit";
              const isSelected =
                drawerOpen &&
                selectedVerse?.number === verse.number &&
                selectedVerse?.chapter === verse.chapter &&
                selectedVerse?.bookName === verse.bookName;
              return (
                <Stack
                  key={idx + "-" + highlightRefresh}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ mb: 2, width: "100%" }}
                >
                  <Button
                    variant="text"
                    onClick={() => handleNavigateToVerse(verse)}
                    sx={{
                      fontWeight: "bold",
                      fontSize: { xs: 16, sm: 18 },
                      minWidth: 0,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      width: { xs: "100%", sm: "auto" },
                      justifyContent: { xs: "flex-start", sm: "center" },
                    }}
                  >
                    {verse.bookName} {verse.chapter}:{verse.number}
                  </Button>
                  <Typography
                    variant="body1"
                    sx={{
                      flex: 1,
                      fontSize: { xs: 15, sm: 17 },
                      cursor: "pointer",
                      backgroundColor: bgColor,
                      borderRadius: 1,
                      px: bgColor !== "inherit" ? 0.5 : 0,
                      py: 1,
                      transition:
                        "background 0.2s, color 0.2s, text-decoration 0.2s",
                      wordBreak: "break-word",
                      width: { xs: "100%", sm: "auto" },
                      textDecoration: "none",
                      color: "inherit",
                      fontWeight: isSelected ? "bold" : "normal",
                      "&:hover": {
                        color: "primary.main",
                        textDecoration: "underline",
                      },
                      display: "inline",
                    }}
                    onClick={() => handleVerseClick(verse)}
                  >
                    {verse.text}
                  </Typography>
                </Stack>
              );
            })}
          </Box>
        </Paper>
      )}
      <VerseDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        verse={selectedVerse}
        bookName={selectedVerse?.bookName}
        chapterNumber={selectedVerse?.chapter}
        highlightColor={highlightColor}
        setHighlightColor={setHighlightColor}
        getHighlight={getHighlight}
        setHighlight={(book, chapter, verse, color) => {
          setHighlight(book, chapter, verse, color);
          setHighlightRefresh((r) => r + 1);
        }}
        onHighlightChange={() => setHighlightRefresh((r) => r + 1)}
        fontType={"default"}
        theme={theme}
        historicalDocs={HISTORICAL_DOCUMENTS}
      />
    </Container>
  );
};

export default TopicalResults;
